/**
 * Mock payloads for every endpoint the active pages call in demo mode.
 *
 * Every value here is obviously fake (`Demo *`, `0xDEM0...`, `demo-N@example.test`)
 * so a screen-share or external preview can never expose real production data.
 */

const NOW = Date.now();
const daysAgo = (n) => new Date(NOW - n * 86_400_000).toISOString();
const minutesAgo = (n) => new Date(NOW - n * 60_000).toISOString();

// ── Permission catalog (matches the migration seed) ──────────────────────
const PERMISSIONS = [
  { id: 1,  group: "Dashboard",        key: "dashboard.view",                 name: "View dashboard" },
  { id: 2,  group: "Dashboard",        key: "dashboard.export",               name: "Export dashboard data" },
  { id: 3,  group: "Notifications",    key: "notifications.view",             name: "View notifications" },
  { id: 4,  group: "Notifications",    key: "notifications.send",             name: "Send notifications" },
  { id: 5,  group: "Transactions",     key: "transactions.view",              name: "View transactions" },
  { id: 6,  group: "Transactions",     key: "transactions.update_status",     name: "Update transaction status" },
  { id: 7,  group: "Cards",            key: "cards.view",                     name: "View cards" },
  { id: 8,  group: "Cards",            key: "cards.verify",                   name: "Verify cards" },
  { id: 9,  group: "Cards",            key: "cards.update_status",            name: "Update card status" },
  { id: 10, group: "Paybis",           key: "paybis.view",                    name: "View Paybis transactions" },
  { id: 11, group: "Paybis",           key: "paybis.sync",                    name: "Sync Paybis volume" },
  { id: 12, group: "Feedback",         key: "feedback.view",                  name: "View feedback / bug reports" },
  { id: 13, group: "Feedback",         key: "feedback.update",                name: "Update feedback status" },
  { id: 14, group: "Feedback",         key: "feedback.delete",                name: "Delete feedback / bug reports" },
  { id: 15, group: "Form Submissions", key: "form_submissions.view",          name: "View form submissions" },
  { id: 16, group: "Form Submissions", key: "form_submissions.update",        name: "Update form submissions" },
  { id: 17, group: "Form Submissions", key: "form_submissions.delete",        name: "Delete form submissions" },
  { id: 18, group: "Form Submissions", key: "form_submissions.export",        name: "Export form submissions" },
  { id: 19, group: "App Metrics",      key: "app_metrics.view",               name: "View app metrics" },
  { id: 20, group: "App Metrics",      key: "app_metrics.sync",               name: "Sync app metrics" },
  { id: 21, group: "App Metrics",      key: "app_metrics.bulk_increment",     name: "Bulk-increment metrics" },
  { id: 22, group: "App Metrics",      key: "app_metrics.external_data",      name: "Modify external metrics data" },
  { id: 23, group: "Alerts",           key: "alerts.view",                    name: "View price alerts" },
  { id: 24, group: "Alerts",           key: "alerts.delete",                  name: "Delete price alerts" },
  { id: 25, group: "Users",            key: "users.view",                     name: "View users" },
  { id: 26, group: "Users",            key: "users.invite",                   name: "Invite users" },
  { id: 27, group: "Users",            key: "users.update_permissions",       name: "Update user permissions" },
  { id: 28, group: "Users",            key: "users.deactivate",               name: "Deactivate users" },
];

const ROLES = [
  { id: 1, key: "super_admin", name: "Super-admin", description: "Full access", is_system: true,
    permissions: PERMISSIONS.map((p) => p.key) },
  { id: 2, key: "admin",       name: "Admin",       description: "Full access except user management", is_system: true,
    permissions: PERMISSIONS.filter((p) => !p.key.startsWith("users.")).map((p) => p.key) },
  { id: 3, key: "viewer",      name: "Viewer",      description: "Read-only", is_system: true,
    permissions: PERMISSIONS.filter((p) => p.key.endsWith(".view")).map((p) => p.key) },
];

const USERS = [
  { id: 0, email: "demo-admin@example.test",   name: "Demo Admin",    is_super_admin: true,  is_active: true,  role: null, last_login_at: minutesAgo(2), created_at: daysAgo(60) },
  { id: 1, email: "demo-viewer@example.test",  name: "Demo Viewer",   is_super_admin: false, is_active: true,  role: ROLES[2], last_login_at: daysAgo(1),  created_at: daysAgo(30) },
  { id: 2, email: "demo-pending@example.test", name: null,            is_super_admin: false, is_active: false, role: ROLES[2], last_login_at: null,        created_at: daysAgo(1)  },
  { id: 3, email: "demo-mgr@example.test",     name: "Demo Manager",  is_super_admin: false, is_active: true,  role: ROLES[1], last_login_at: daysAgo(5),  created_at: daysAgo(45) },
];

// ── Active page payloads ─────────────────────────────────────────────────

const demoData = {
  permissions: () => ({ status: 200, permissions: PERMISSIONS }),
  roles: () => ({ status: 200, roles: ROLES }),
  users: () => ({ status: 200, users: USERS }),
  userById: (id) => {
    const u = USERS.find((x) => x.id === id);
    if (!u) return { status: 404, message: "User not found", user: null };
    const role = u.role || ROLES[0];
    return {
      status: 200,
      user: {
        ...u,
        role,
        effective_permissions: role.permissions,
        overrides: [],
      },
    };
  },

  // Overview / Dashboard
  dashboardOverview: () => ({
    status: 200,
    data: {
      total_users: 1234,
      total_transactions: 5678,
      total_revenue_usd: 89_540,
      active_alerts: 12,
      recent_transactions: [
        { id: "demo-tx-1", type: "SEND",   amount: "0.5",  symbol: "BIORGOV", status: "completed", created_at: minutesAgo(5)  },
        { id: "demo-tx-2", type: "SWAP",   amount: "120",  symbol: "USDC",    status: "completed", created_at: minutesAgo(28) },
        { id: "demo-tx-3", type: "ON-RAMP",amount: "1000", symbol: "USD",     status: "pending",   created_at: minutesAgo(60) },
      ],
    },
  }),
  transactionAnalytics: () => ({
    status: 200,
    data: {
      time_series: Array.from({ length: 14 }).map((_, i) => ({
        date: daysAgo(13 - i).slice(0, 10),
        send: Math.round(20 + Math.random() * 30),
        swap: Math.round(10 + Math.random() * 20),
        ramp: Math.round(2  + Math.random() * 10),
      })),
      distribution: { SEND: 60, SWAP: 25, ONRAMP: 10, OFFRAMP: 5 },
    },
  }),
  metricsDashboard: () => ({
    status: 200,
    data: {
      transaction_count: 5678,
      card_creation_count: 142,
      website_visits: 18_402,
      nft_minted: 73,
      first_wallet: 945,
      first_swap: 612,
      byb_token_volume: 245_812,
    },
  }),

  // Notifications
  notifications: () => ({
    status: 200,
    notifications: [
      { id: "demo-n-1", title: "Demo welcome banner",       message: "Sample push notification body",  audience: "All Users",     type: "GENERAL",  push: true,  inapp: true,  status: "sent",      sent_at: minutesAgo(15) },
      { id: "demo-n-2", title: "Scheduled maintenance",     message: "Demo scheduled message preview", audience: "iOS users",     type: "GENERAL",  push: false, inapp: true,  status: "scheduled", scheduled_at: daysAgo(-1) },
      { id: "demo-n-3", title: "Price alert triggered",     message: "Demo alert preview",             audience: "Android users", type: "ALERT",    push: true,  inapp: true,  status: "sent",      sent_at: daysAgo(2) },
    ],
    sent: [], scheduled: [],
  }),

  // Transactions
  transactionsHistory: () => ({
    status: 200,
    data: {
      transactions: [
        { id: "demo-tx-1", type: "SEND",  from_address: "0xDEM0AAA00000000000000000000000000DEM01", to_address: "0xDEM0BBB00000000000000000000000000DEM02", amount: "0.5",   token_symbol: "ETH",     status: "completed", created_at: minutesAgo(5)   },
        { id: "demo-tx-2", type: "SWAP",  from_address: "0xDEM0CCC00000000000000000000000000DEM03", to_address: "0xDEM0DDD00000000000000000000000000DEM04", amount: "120",   token_symbol: "USDC",    status: "completed", created_at: minutesAgo(28)  },
        { id: "demo-tx-3", type: "ON-RAMP", from_address: "—",                                       to_address: "0xDEM0EEE00000000000000000000000000DEM05", amount: "1000",  token_symbol: "USD",     status: "pending",   created_at: minutesAgo(60)  },
        { id: "demo-tx-4", type: "OFF-RAMP",from_address: "0xDEM0FFF00000000000000000000000000DEM06",to_address: "—",                                        amount: "250",   token_symbol: "USD",     status: "completed", created_at: daysAgo(1)      },
        { id: "demo-tx-5", type: "TRANSFER",from_address: "0xDEM0GGG00000000000000000000000000DEM07",to_address: "0xDEM0HHH00000000000000000000000000DEM08", amount: "42.10", token_symbol: "BIORGOV", status: "failed",    created_at: daysAgo(2)      },
      ],
      pagination: { total: 5, limit: 50, offset: 0 },
    },
  }),
  transactionsStats: () => ({
    status: 200,
    data: { total: 5, completed: 3, pending: 1, failed: 1, total_volume_usd: 42_000 },
  }),

  // Paybis
  paybisList: () => ({
    status: 200,
    data: {
      transactions: [
        { id: "demo-pb-1", flow: "on_ramp",  status: "completed", amount: "500",  fiat_currency: "USD", crypto_currency: "BTC", customer_email: "demo-1@example.test", created_at: minutesAgo(45) },
        { id: "demo-pb-2", flow: "on_ramp",  status: "pending",   amount: "1000", fiat_currency: "EUR", crypto_currency: "ETH", customer_email: "demo-2@example.test", created_at: daysAgo(1)     },
        { id: "demo-pb-3", flow: "off_ramp", status: "completed", amount: "250",  fiat_currency: "USD", crypto_currency: "USDC", customer_email: "demo-3@example.test", created_at: daysAgo(3)    },
      ],
      pagination: { total: 3, limit: 50, offset: 0 },
    },
  }),
  paybisHistory: () => ({ status: 200, data: { transactions: [], pagination: { total: 0, limit: 50, offset: 0 } } }),
  paybisStats:   () => ({ status: 200, data: { total: 16, completed: 11, pending: 3, failed: 2, total_volume_usd: 18_400 } }),

  // App Metrics
  appMetricsSummary: () => ({
    status: 200,
    data: {
      total_downloads: 24_891,
      active_users: 9_402,
      avg_crash_rate: 0.42,
      total_revenue: 14_220,
      platforms: { ios: 14_550, android: 10_341 },
    },
  }),
  appMetricsGrowth: () => ({
    status: 200,
    data: Array.from({ length: 14 }).map((_, i) => ({
      date: daysAgo(13 - i).slice(0, 10),
      downloads: Math.round(120 + Math.random() * 180),
    })),
  }),
  appMetricsPlatform: (platform) => ({
    status: 200,
    data: { platform, total_downloads: platform === "ios" ? 14_550 : 10_341, daily: [] },
  }),

  // Alerts
  alerts: () => ({
    status: 200,
    data: {
      alerts: [
        { id: 1001, user_id: "demo-user-1", coin_symbol: "ETH",     coin_name: "Ethereum", condition: "above",     target_price: 3500, percentage_threshold: null, upper_bound: null, lower_bound: null, is_active: true,  is_triggered: false, created_at: daysAgo(2) },
        { id: 1002, user_id: "demo-user-2", coin_symbol: "BIORGOV", coin_name: "BiorGov",  condition: "below",     target_price: 0.12, percentage_threshold: null, upper_bound: null, lower_bound: null, is_active: true,  is_triggered: true,  created_at: daysAgo(5) },
        { id: 1003, user_id: "demo-user-3", coin_symbol: "BTC",     coin_name: "Bitcoin",  condition: "crossing",  target_price: null, percentage_threshold: null, upper_bound: 70000, lower_bound: 60000, is_active: false, is_triggered: false, created_at: daysAgo(7) },
      ],
      pagination: { total: 3, limit: 100, offset: 0 },
    },
  }),

  // Feedback
  feedbackCategories: () => ({ status: 200, categories: [
    { id: 1, name: "Demo: UI",      display_order: 1 },
    { id: 2, name: "Demo: Wallet",  display_order: 2 },
    { id: 3, name: "Demo: Swap",    display_order: 3 },
  ] }),
  feedbackSuggestions: () => ({
    status: 200,
    data: {
      suggestions: [
        { id: 5001, title: "Demo: dark theme everywhere", description: "Sample suggestion body — placeholder text only.", status: "pending",  upvotes: 12, category_id: 1, email: "demo-1@example.test", images: [], created_at: minutesAgo(45) },
        { id: 5002, title: "Demo: faster swap quotes",    description: "Sample suggestion body — placeholder text only.", status: "approved", upvotes: 41, category_id: 3, email: "demo-2@example.test", images: [], created_at: daysAgo(2)     },
        { id: 5003, title: "Demo: import private key",    description: "Sample suggestion body — placeholder text only.", status: "in_review", upvotes: 8, category_id: 2, email: "demo-3@example.test", images: [], created_at: daysAgo(4)     },
      ],
      pagination: { total: 3, limit: 50, offset: 0 },
    },
  }),
  feedbackBugReports: () => ({
    status: 200,
    data: {
      bug_reports: [
        { id: 7001, description: "Demo: dropdown closes on tab-switch (sample report)",  email: "demo-1@example.test", images: [], status: "pending", created_at: minutesAgo(20) },
        { id: 7002, description: "Demo: chart axis labels overflow on small viewports", email: "demo-2@example.test", images: [], status: "fixed",   created_at: daysAgo(3)     },
      ],
      pagination: { total: 2, limit: 50, offset: 0 },
    },
  }),
  feedbackDoNotLike: () => ({
    status: 200,
    data: {
      items: [
        { id: 9001, description: "Demo: too many notifications by default",  email: "demo-3@example.test", images: [], created_at: daysAgo(6) },
      ],
      pagination: { total: 1, limit: 50, offset: 0 },
    },
  }),

  // Form submissions (marketing inbox)
  // Shape mirrors the backend exactly: list = {total, limit, offset, items: [...]}
  // getOne = the record directly. stats = {total, byType, byStatus}.
  formSubmissionsList: () => ({
    total: 3, limit: 50, offset: 0,
    items: [
      { id: "demo-fs-1", type: "token",       status: "new",       notes: "",                    archivedAt: null, submittedAt: minutesAgo(10), updatedAt: minutesAgo(10), data: { projectName: "Demo Token Project",   contactName: "Demo Sender",       contactEmail: "demo-1@example.test", website: "https://example.test", message: "Sample inbound — placeholder text only." } },
      { id: "demo-fs-2", type: "integration", status: "reviewing", notes: "Demo internal note.", archivedAt: null, submittedAt: daysAgo(1),     updatedAt: minutesAgo(60), data: { orgName: "Demo Integrator Inc.",     contactName: "Demo Integrator",   contactEmail: "demo-2@example.test", telegram: "@demo_handle",        message: "Sample integration request — placeholder text only." } },
      { id: "demo-fs-3", type: "collab",      status: "done",      notes: "",                    archivedAt: null, submittedAt: daysAgo(4),     updatedAt: daysAgo(2),     data: { topic: "Demo collab",                contactName: "Demo Collaborator", contactEmail: "demo-3@example.test",                                  message: "Sample collab inquiry — placeholder text only." } },
    ],
  }),
  formSubmissionsOne: (id) => ({
    id: id || "demo-fs-1",
    type: "token",
    status: "new",
    notes: "",
    archivedAt: null,
    submittedAt: minutesAgo(10),
    updatedAt: minutesAgo(10),
    data: {
      projectName: "Demo Token Project",
      contactName: "Demo Sender",
      contactEmail: "demo-1@example.test",
      website: "https://example.test",
      message: "Sample inbound — placeholder text only.",
    },
  }),
  formSubmissionsStats: () => ({
    total: 3,
    active: 3,
    archived: 0,
    byType:   { token: 1, integration: 1, collab: 1, dapp: 0, general: 0 },
    byStatus: { new: 1, reviewing: 1, done: 1, spam: 0 },
  }),

  // North Star — Weekly Engaged Wallets. Contract-shaped (top-level fields),
  // so demo mode renders a populated WEW command center.
  wewSummary: () => ({
    as_of: new Date().toISOString(),
    window_days: 7,
    wew: 68, wew_prev: 61,
    wew_swap_dominant: 41, wew_card_dominant: 27,
    installed_total: 612, wew_installed_ratio: 0.111, wew_stable_usd: 53_200,
  }),
  wewTrend: () => ({
    weeks: 12,
    series: Array.from({ length: 12 }).map((_, i) => ({
      week_start: daysAgo((11 - i) * 7).slice(0, 10),
      wew: Math.round(28 + i * 3.4 + Math.random() * 6),
      swap_dominant: Math.round(20 + i * 1.8),
      card_dominant: Math.round(6 + i * 1.6),
    })),
  }),
  wewPass: () => ({
    as_of: new Date().toISOString(),
    conditions: [
      { id: "wew_count",     label: "WEW",             current: 68,    target: 75,   unit: "count", status: "warn",  alarm_below: null },
      { id: "wew_ratio",     label: "WEW / installed", current: 0.111, target: 0.10, unit: "ratio", status: "pass",  alarm_below: null },
      { id: "w1_retention",  label: "W1 retention",    current: 0.28,  target: 0.30, unit: "ratio", status: "warn",  alarm_below: 0.25 },
      { id: "submetric_max", label: "Top sub-metric",  current: 41,    target: 50,   unit: "count", status: "warn",  alarm_below: null },
    ],
  }),
  wewRetention: () => ({
    weeks: 8,
    cohorts: Array.from({ length: 8 }).map((_, i) => ({
      cohort_week: daysAgo((7 - i) * 7).slice(0, 10),
      size: Math.round(60 + Math.random() * 80),
      retention: [1.0, 0.3 + Math.random() * 0.1, 0.18 + Math.random() * 0.08, 0.12 + Math.random() * 0.06].slice(0, Math.max(1, 8 - i)),
    })),
  }),
  wewCountermeasures: () => ({
    stable_balance_30d: { series: Array.from({ length: 30 }).map((_, i) => ({ date: daysAgo(29 - i).slice(0, 10), avg_balance_usd: 38 + Math.random() * 12 })) },
    smile_curve: { deciles: Array.from({ length: 10 }).map((_, i) => ({ decile: i + 1, retained_pct: 0.12 + i * 0.06 })) },
    time_to_first_action: { buckets: [
      { label: "<1h", count: 30 }, { label: "1-24h", count: 88 }, { label: "1-3d", count: 41 }, { label: ">3d", count: 22 },
    ] },
    wew_stable_usd: { current: 53_200, series: Array.from({ length: 12 }).map((_, i) => ({ week_start: daysAgo((11 - i) * 7).slice(0, 10), usd: 26_000 + i * 2_400 })) },
  }),
};

export default demoData;
